import {
  type Accessor,
  createContext,
  createSignal,
  type ParentComponent,
  type Setter,
  useContext,
} from "solid-js";
import { usePostHog } from "@/hooks/usePostHog";
import { useVisitedJobStore } from "../store/visited_job";
import type { JobOfferV2WithCompanyId } from "../types";

export type SelectedJob = {
  job: JobOfferV2WithCompanyId;
  isAlreadyApplied: boolean;
};

export interface JobListContextValue {
  selectedJob: Accessor<SelectedJob | undefined>;
  setSelectedJob: Setter<SelectedJob | undefined>;
  isSheetOpen: Accessor<boolean>;
  setIsSheetOpen: Setter<boolean>;
  handleJobCardClick: (
    job: JobOfferV2WithCompanyId,
    isAlreadyApplied: boolean,
  ) => void;
}

const JobListContext = createContext<JobListContextValue | undefined>(
  undefined,
);

export const useJobListContext = () => {
  const context = useContext(JobListContext);
  if (!context) {
    throw new Error("useJobListContext must be used within a JobListProvider");
  }
  return context;
};

export const useJobListContextOptional = () => {
  return useContext(JobListContext);
};

export const JobListProvider: ParentComponent = (props) => {
  const { capture } = usePostHog();

  const [selectedJob, setSelectedJob] = createSignal<SelectedJob | undefined>(
    undefined,
  );
  const { addVisitedJob, isJobVisited } = useVisitedJobStore();

  const [isSheetOpen, setIsSheetOpen] = createSignal(false);
  const handleJobCardClick = (
    job: JobOfferV2WithCompanyId,
    isAlreadyApplied: boolean,
  ) => {
    setSelectedJob({ job, isAlreadyApplied });
    setIsSheetOpen(true);
    if (isAlreadyApplied) {
      capture("view_applied_job_offer", {
        job_offer_id: job.id,
      });
    } else {
      capture("view_job_offer", {
        job_offer_id: job.id,
      });
    }

    if (!isJobVisited(job.id)) {
      addVisitedJob(job.id);
    }
  };

  return (
    <JobListContext.Provider
      value={{
        selectedJob,
        setSelectedJob,
        isSheetOpen,
        setIsSheetOpen,
        handleJobCardClick,
      }}
    >
      {props.children}
    </JobListContext.Provider>
  );
};
